/**
 * <AgendarSofia />
 * ---------------------------------------------------------------------------
 * Botão flutuante fixo "Quero mais informações" + modal de 3 etapas:
 *   1. Dados (nome, telefone, e-mail)
 *   2. Próximo passo (consultoria com especialista | visita ao decorado)
 *   3. Data e hora → cria o lead AGENDADO na Bianca e dispara o template
 *      oficial de confirmação (via edge function `sofia-agendar`).
 *
 * Design system da LP Sofia: Playfair Display italic / Inter, fundo #0a070b,
 * dourado #e9c87a, cream #faf3e0. styled-components + framer-motion.
 *
 * Os horários vêm da `disponibilidade` real do produto Sofia (mesma fonte do
 * agente de agendamento), via fetchAvailability/parseAvailability.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  supabase,
  fetchAvailability,
  parseAvailability,
  getDayName,
} from '../../../services/supabaseClient';
import { trackSofiaLead, trackSofiaSchedule } from '../pixel';

const PRODUCT_ID = 'B1sne5dPUYVDFDXQ'; // Sofia (tenant Manu Imóveis)

const MODALIDADES = [
  {
    id: 'consultoria',
    titulo: 'Consultoria com especialista',
    desc: 'Um especialista te liga para entender o que você procura e apresentar o Sofia.',
  },
  {
    id: 'visita',
    titulo: 'Visita ao decorado',
    desc: 'Conheça o apartamento decorado pessoalmente, com hora marcada.',
  },
];

/* ---------------------------------------------------------------- helpers */

const maskPhone = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};
const phoneDigits = (v) => (v || '').replace(/\D/g, '');
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '');
const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const WEEK_ABBR = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/* ---------------------------------------------------------------- styles */

const FloatBtn = styled(motion.button)`
  position: fixed;
  /* centra por auto-margin (NÃO por transform: translateX) — o framer-motion
     escreve transform pra animar y/opacity e sobrescreveria o translateX. */
  left: 0;
  right: 0;
  margin: 0 auto;
  width: max-content;
  max-width: calc(100vw - 28px);
  bottom: calc(env(safe-area-inset-bottom, 0px) + 26px);
  z-index: 900;
  border: none;
  cursor: pointer;
  padding: 13px 30px;
  border-radius: 999px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #2a1d0f;
  white-space: nowrap;
  background: linear-gradient(135deg, #f3d99a 0%, #e9c87a 48%, #d8b063 100%);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(250, 243, 224, 0.25) inset,
    0 0 20px rgba(233, 200, 122, 0.3);

  @media (min-width: 768px) {
    bottom: calc(env(safe-area-inset-bottom, 0px) + 32px);
    font-size: 13.5px;
    padding: 15px 34px;
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  background: rgba(7, 4, 8, 0.74);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);

  @media (min-width: 768px) {
    align-items: center;
    padding: 24px;
  }
`;

const Card = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: 92dvh;
  overflow-y: auto;
  background: linear-gradient(180deg, #17101a 0%, #0c0810 100%);
  border: 1px solid rgba(233, 200, 122, 0.22);
  border-bottom: none;
  border-radius: 26px 26px 0 0;
  padding: 26px 22px calc(env(safe-area-inset-bottom, 0px) + 26px);
  color: #f5e8c8;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  @media (min-width: 768px) {
    border-bottom: 1px solid rgba(233, 200, 122, 0.22);
    border-radius: 22px;
    padding: 32px 30px;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(245, 232, 200, 0.18);
  background: rgba(245, 232, 200, 0.04);
  color: rgba(245, 232, 200, 0.75);
  font-size: 17px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { color: #faf3e0; border-color: rgba(250, 215, 140, 0.6); }
`;

const Dots = styled.div`
  display: flex;
  gap: 7px;
  margin-bottom: 18px;
`;
const Dot = styled.span`
  width: ${({ $active }) => ($active ? '22px' : '7px')};
  height: 7px;
  border-radius: 999px;
  background: ${({ $active, $done }) =>
    $active ? '#e9c87a' : $done ? 'rgba(233,200,122,0.55)' : 'rgba(245,232,200,0.18)'};
  transition: all 320ms ease;
`;

const Eyebrow = styled.div`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 10px;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: #e9c87a;
  opacity: 0.9;
  margin-bottom: 8px;
`;
const Title = styled.h2`
  font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  font-size: clamp(26px, 7vw, 34px);
  line-height: 1.05;
  color: #faf3e0;
  margin: 0 0 6px;
`;
const Sub = styled.p`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(245, 232, 200, 0.65);
  margin: 0 0 22px;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;
const FieldLabel = styled.label`
  display: block;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(245, 232, 200, 0.55);
  margin-bottom: 7px;
`;
const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 14px 16px;
  border-radius: 13px;
  background: rgba(245, 232, 200, 0.05);
  border: 1px solid ${({ $err }) => ($err ? 'rgba(220,120,90,0.7)' : 'rgba(245,232,200,0.14)')};
  color: #faf3e0;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 16px; /* 16px evita zoom no iOS */
  outline: none;
  transition: border-color 200ms ease, background 200ms ease;
  &::placeholder { color: rgba(245, 232, 200, 0.3); }
  &:focus { border-color: #e9c87a; background: rgba(245, 232, 200, 0.08); }
`;

const ModeCard = styled.button`
  width: 100%;
  text-align: left;
  display: block;
  padding: 18px;
  margin-bottom: 12px;
  border-radius: 16px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? 'rgba(233,200,122,0.12)' : 'rgba(245,232,200,0.04)')};
  border: 1px solid ${({ $active }) => ($active ? '#e9c87a' : 'rgba(245,232,200,0.14)')};
  transition: all 200ms ease;
  &:hover { border-color: rgba(233, 200, 122, 0.6); }
`;
const ModeTitle = styled.div`
  font-family: 'Playfair Display', Georgia, serif;
  font-style: italic;
  font-size: 19px;
  color: #faf3e0;
  margin-bottom: 5px;
`;
const ModeDesc = styled.div`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12.5px;
  line-height: 1.45;
  color: rgba(245, 232, 200, 0.6);
`;

const DayRail = styled.div`
  display: flex;
  gap: 9px;
  overflow-x: auto;
  padding-bottom: 6px;
  margin-bottom: 18px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;
const DayChip = styled.button`
  flex: 0 0 auto;
  min-width: 62px;
  padding: 11px 8px;
  border-radius: 13px;
  cursor: pointer;
  text-align: center;
  background: ${({ $active }) => ($active ? 'rgba(233,200,122,0.14)' : 'rgba(245,232,200,0.04)')};
  border: 1px solid ${({ $active }) => ($active ? '#e9c87a' : 'rgba(245,232,200,0.12)')};
  color: ${({ $active }) => ($active ? '#faf3e0' : 'rgba(245,232,200,0.7)')};
  font-family: 'Inter', system-ui, sans-serif;
  transition: all 180ms ease;
`;
const DayAbbr = styled.div`
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.7;
  margin-bottom: 3px;
`;
const DayNum = styled.div`
  font-size: 17px;
  font-weight: 600;
`;

const SlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 9px;
  margin-bottom: 8px;
`;
const Slot = styled.button`
  padding: 11px 0;
  border-radius: 11px;
  cursor: pointer;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 13.5px;
  background: ${({ $active }) => ($active ? '#e9c87a' : 'rgba(245,232,200,0.05)')};
  border: 1px solid ${({ $active }) => ($active ? '#e9c87a' : 'rgba(245,232,200,0.12)')};
  color: ${({ $active }) => ($active ? '#2a1d0f' : 'rgba(245,232,200,0.8)')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  transition: all 160ms ease;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 22px;
`;
const Primary = styled.button`
  flex: 1;
  padding: 15px;
  border: none;
  border-radius: 13px;
  cursor: pointer;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #2a1d0f;
  background: linear-gradient(135deg, #f3d99a 0%, #e9c87a 50%, #d8b063 100%);
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  transition: opacity 200ms ease;
`;
const Ghost = styled.button`
  padding: 15px 20px;
  border-radius: 13px;
  cursor: pointer;
  background: transparent;
  border: 1px solid rgba(245, 232, 200, 0.18);
  color: rgba(245, 232, 200, 0.7);
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  &:hover { color: #faf3e0; border-color: rgba(250, 215, 140, 0.5); }
`;

const Note = styled.div`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  line-height: 1.45;
  color: ${({ $err }) => ($err ? 'rgba(232,150,120,0.95)' : 'rgba(245,232,200,0.5)')};
  margin-top: 12px;
  text-align: center;
`;

const Success = styled.div`
  text-align: center;
  padding: 14px 4px 6px;
`;
const Check = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  color: #2a1d0f;
  background: linear-gradient(135deg, #f3d99a, #e9c87a);
  box-shadow: 0 0 30px rgba(233, 200, 122, 0.35);
`;

/* ---------------------------------------------------------------- component */

export default function AgendarSofia() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1..3 ; 4 = sucesso
  const [form, setForm] = useState({ nome: '', telefone: '', email: '', modalidade: '' });
  const [day, setDay] = useState(null); // { ymd, ... }
  const [time, setTime] = useState(null); // 'HH:MM'
  const [avail, setAvail] = useState(null);
  const [availErr, setAvailErr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  // esconde o botão flutuante quando o CTA do take final (CondominioScrub) aparece
  const [hideFloat, setHideFloat] = useState(false);
  // botão só aparece (fade-in) depois da primeira rolada — no hero ele competia
  // com a barra do Safari no iPhone e ficava ruim.
  const [scrolled, setScrolled] = useState(false);

  const overflowYRef = useRef('');
  const openRef = useRef(() => {});

  // trava scroll do fundo sem mexer no overflowX (que o SofiaLP usa pro sticky)
  useEffect(() => {
    if (open) {
      overflowYRef.current = document.body.style.overflowY;
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = overflowYRef.current || '';
    }
    return () => { document.body.style.overflowY = overflowYRef.current || ''; };
  }, [open]);

  // Esc fecha (desktop)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // carrega disponibilidade ao chegar no passo 3
  useEffect(() => {
    if (step !== 3 || avail) return;
    let alive = true;
    (async () => {
      const res = await fetchAvailability(PRODUCT_ID);
      if (!alive) return;
      if (!res?.disponibilidade) { setAvailErr(true); return; }
      const parsed = parseAvailability(res.disponibilidade, null);
      setAvail(parsed);
    })();
    return () => { alive = false; };
  }, [step, avail]);

  // próximos 14 dias com slots na disponibilidade do produto
  const days = useMemo(() => {
    if (!avail) return [];
    const out = [];
    for (let i = 1; i <= 14; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const name = getDayName(d); // 'Segunda', 'Terça'... 'Domingo'/'Sábado'
      const ranges = avail[name] || [];
      if (!ranges.length) continue;
      const slots = ranges.map((r) => r.split('-')[0]); // start HH:MM
      out.push({ ymd: toYMD(d), abbr: WEEK_ABBR[d.getDay()], num: d.getDate(), slots });
    }
    return out;
  }, [avail]);

  const slotsForDay = useMemo(() => {
    if (!day) return [];
    return days.find((d) => d.ymd === day)?.slots || [];
  }, [day, days]);

  const reset = () => {
    setStep(1);
    setForm({ nome: '', telefone: '', email: '', modalidade: '' });
    setDay(null); setTime(null); setSubmitErr(''); setSubmitting(false);
  };
  const close = () => { setOpen(false); };
  const openModal = () => { reset(); setOpen(true); };
  openRef.current = openModal;

  // detecta a primeira rolada pra revelar o botão (fade-in) após o hero
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 90);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // eventos do CTA final (CondominioScrub): abrir o modal + esconder o flutuante
  useEffect(() => {
    const onOpen = () => openRef.current();
    const onCta = (e) => setHideFloat(!!e.detail);
    window.addEventListener('sofia-open-agendar', onOpen);
    window.addEventListener('sofia-cta-visible', onCta);
    return () => {
      window.removeEventListener('sofia-open-agendar', onOpen);
      window.removeEventListener('sofia-cta-visible', onCta);
    };
  }, []);

  const step1Ok =
    form.nome.trim().length >= 2 && phoneDigits(form.telefone).length >= 10 && emailOk(form.email);

  // Captura do lead no passo 1 (SPEC 70): preencheu o formulário = "já é lead".
  // Grava o pré-lead no banco + dispara o evento Lead (pixel + CAPI). Roda 1x só,
  // mesmo se o usuário voltar e avançar de novo. Best-effort: nunca bloqueia o fluxo.
  const preleadSentRef = useRef(false);

  const avancarParaPasso2 = () => {
    setStep(2);
    if (preleadSentRef.current) return;
    preleadSentRef.current = true;

    const leadEventId = (crypto?.randomUUID?.() || String(Date.now()) + Math.random());
    trackSofiaLead({ eventId: leadEventId });

    supabase.functions
      .invoke('sofia-prelead', {
        body: {
          nome: form.nome.trim(),
          telefone: form.telefone,
          email: form.email.trim(),
          event_id: leadEventId,
          event_source_url: typeof window !== 'undefined' ? window.location.href : '',
        },
      })
      .catch(() => {
        // falha no pré-lead não pode travar o agendamento; o cron de fallback
        // só atua sobre o que foi gravado, então no pior caso esse lead segue
        // pelo fluxo de agendamento normal se ele concluir o passo 3.
      });
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitErr('');
    // id único compartilhado entre o pixel do navegador e a CAPI server-side (dedup Meta)
    const eventId = (crypto?.randomUUID?.() || String(Date.now()) + Math.random());
    try {
      const { data, error } = await supabase.functions.invoke('sofia-agendar', {
        body: {
          nome: form.nome.trim(),
          telefone: form.telefone,
          email: form.email.trim(),
          modalidade: form.modalidade,
          dateYMD: day,
          time,
          event_id: eventId,
          event_source_url: typeof window !== 'undefined' ? window.location.href : '',
        },
      });
      let payload = data;
      if (error) {
        // edge function devolveu 4xx/5xx — lê o corpo pra mensagem real
        try { payload = await error.context.json(); } catch { payload = null; }
      }
      if (payload?.success) {
        // conversão principal (Meta Pixel) — agendamento concluído = evento Schedule.
        // mesmo eventId da CAPI server-side pra o Meta deduplicar.
        trackSofiaSchedule({ eventId, content_category: form.modalidade });
        setStep(4);
      } else {
        setSubmitErr(
          payload?.template_error || payload?.error ||
          'Não consegui concluir agora. Tente novamente em instantes.'
        );
      }
    } catch (e) {
      setSubmitErr('Falha de conexão. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const setF = (k) => (e) => {
    const v = k === 'telefone' ? maskPhone(e.target.value) : e.target.value;
    setForm((s) => ({ ...s, [k]: v }));
  };

  return (
    <>
      <AnimatePresence>
        {!open && !hideFloat && scrolled && (
          <FloatBtn
            key="float"
            onClick={openModal}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            whileTap={{ scale: 0.96 }}
          >
            Quero mais informações
          </FloatBtn>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <Overlay
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Card
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            >
              <CloseBtn onClick={close} aria-label="Fechar">×</CloseBtn>

              {step < 4 && (
                <Dots>
                  {[1, 2, 3].map((n) => (
                    <Dot key={n} $active={step === n} $done={step > n} />
                  ))}
                </Dots>
              )}

              <AnimatePresence mode="wait">
                {/* STEP 1 — dados */}
                {step === 1 && (
                  <motion.div key="s1"
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.28 }}
                  >
                    <Eyebrow>Sofia · Campo Belo</Eyebrow>
                    <Title>Vamos te apresentar o Sofia</Title>
                    <Sub>Deixe seus dados e escolha como prefere seguir.</Sub>

                    <Field>
                      <FieldLabel>Nome</FieldLabel>
                      <Input value={form.nome} onChange={setF('nome')} placeholder="Seu nome completo" autoComplete="name" />
                    </Field>
                    <Field>
                      <FieldLabel>WhatsApp</FieldLabel>
                      <Input value={form.telefone} onChange={setF('telefone')} placeholder="(11) 99999-9999"
                        inputMode="tel" autoComplete="tel" />
                    </Field>
                    <Field>
                      <FieldLabel>E-mail</FieldLabel>
                      <Input value={form.email} onChange={setF('email')} placeholder="voce@email.com"
                        inputMode="email" autoComplete="email"
                        $err={form.email.length > 4 && !emailOk(form.email)} />
                    </Field>

                    <Actions>
                      <Primary disabled={!step1Ok} onClick={avancarParaPasso2}>Continuar</Primary>
                    </Actions>
                  </motion.div>
                )}

                {/* STEP 2 — modalidade */}
                {step === 2 && (
                  <motion.div key="s2"
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.28 }}
                  >
                    <Eyebrow>Próximo passo</Eyebrow>
                    <Title>Como prefere seguir?</Title>
                    <Sub>Escolha o formato ideal para você conhecer o empreendimento.</Sub>

                    {MODALIDADES.map((m) => (
                      <ModeCard key={m.id} $active={form.modalidade === m.id}
                        onClick={() => setForm((s) => ({ ...s, modalidade: m.id }))}>
                        <ModeTitle>{m.titulo}</ModeTitle>
                        <ModeDesc>{m.desc}</ModeDesc>
                      </ModeCard>
                    ))}

                    <Actions>
                      <Ghost onClick={() => setStep(1)}>Voltar</Ghost>
                      <Primary disabled={!form.modalidade} onClick={() => setStep(3)}>Continuar</Primary>
                    </Actions>
                  </motion.div>
                )}

                {/* STEP 3 — data e hora */}
                {step === 3 && (
                  <motion.div key="s3"
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.28 }}
                  >
                    <Eyebrow>{form.modalidade === 'consultoria' ? 'Consultoria' : 'Visita ao decorado'}</Eyebrow>
                    <Title>Escolha dia e horário</Title>
                    <Sub>Selecione o melhor momento — você recebe a confirmação no WhatsApp.</Sub>

                    {availErr && <Note $err>Não consegui carregar os horários. Tente de novo.</Note>}
                    {!avail && !availErr && <Note>Carregando horários…</Note>}

                    {avail && days.length > 0 && (
                      <>
                        <DayRail>
                          {days.map((d) => (
                            <DayChip key={d.ymd} $active={day === d.ymd}
                              onClick={() => { setDay(d.ymd); setTime(null); }}>
                              <DayAbbr>{d.abbr}</DayAbbr>
                              <DayNum>{d.num}</DayNum>
                            </DayChip>
                          ))}
                        </DayRail>

                        {day && (
                          <SlotGrid>
                            {slotsForDay.map((t) => (
                              <Slot key={t} $active={time === t} onClick={() => setTime(t)}>{t}</Slot>
                            ))}
                          </SlotGrid>
                        )}
                      </>
                    )}

                    {submitErr && <Note $err>{submitErr}</Note>}

                    <Actions>
                      <Ghost onClick={() => setStep(2)}>Voltar</Ghost>
                      <Primary disabled={!day || !time || submitting} onClick={submit}>
                        {submitting ? 'Confirmando…' : 'Confirmar agendamento'}
                      </Primary>
                    </Actions>
                  </motion.div>
                )}

                {/* STEP 4 — sucesso */}
                {step === 4 && (
                  <motion.div key="s4"
                    initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Success>
                      <Check>✓</Check>
                      <Title>Agendamento confirmado</Title>
                      <Sub style={{ marginBottom: 8 }}>
                        Enviamos a confirmação no seu WhatsApp. Até breve!
                      </Sub>
                      <Actions>
                        <Primary onClick={close}>Fechar</Primary>
                      </Actions>
                    </Success>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </Overlay>
        )}
      </AnimatePresence>
    </>
  );
}
